import {
	createContext,
	Dispatch,
	ReactNode,
	SetStateAction,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState
} from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { updatePractice, useDisputesByEvidenceNeeded } from '../util/db'
import { fetchPostJSON } from '../util/api-helpers'
import { selectContext, selectIsAuthCheckLoading } from '../selectors/auth'
import { PLANS_FILTER_ON_OPTIONS, PLANS_QUICK_FILTERS, STATUS_SUCCESS } from '../constants'
import CustomizedLabels from '../util/CustomizedLabels'
import customFields from '../util/customFields'
import { isScratchAdminUser } from '../util/user'
import { fetchAppointmentRequestsCount } from '../slices/apptRequests'
import { AppDispatch } from '../store'
import { usePractices, getConsolidator } from '../util/unified'
import { browserLocalPersistence, browserSessionPersistence, setPersistence } from 'firebase/auth'
import { authClient } from '../lib/firebase'
import { useSearchParams } from 'react-router-dom'
import { format, subDays } from 'date-fns'
import { PimsIndustry } from 'gql/generated-types/custom-graphql'
import { stringToEnumValue } from 'util/enum-helper'
import { useConsolidatedPractice } from 'hooks/useConsolidatedPracticeAsSource'
import { hasFinancingDashboardAccess } from 'util/practice'

const PRACTICE_ID_STORAGE_KEY = 'selectedPracticeId'

export interface PracticeContext {
	currentPracticeObject: IPractice // IPractice
	practiceID?: string
	terminalLocation?: string
	userRolePractice?: UserRolePractice
	clinicName?: string
	disputesNotificationCount?: number
	currentConsolidator?: IConsolidator
	currentUserConsolidator?: IConsolidator
	currentPracticeIndustry?: PimsIndustry
	dashboardUserId?: number
	isCurrentPracticeActive?: boolean
	showPayments: boolean
	isPaymentEnabled?: boolean
	isFinancingEnabled?: boolean
	canAccessFinancingDev?: boolean

	// TODO: Should these be on the Context.Provider below?
	setCurrentPracticeObject?: any
	setPracticeID?: any
	planFilterOptions: PracticePlanFilterOptions
	setPlanFilterOptions?: Dispatch<SetStateAction<PracticePlanFilterOptions>>
	quickPlanFilterSetter?: (filter: PracticePlanFilterOptions['quickFilterValue']) => () => void
	clearPlanFilters?: () => void
}

const getOlderDate = (minusDays = 0) => format(subDays(new Date(), minusDays), 'yyyy-MM-dd')

const CurrentPracticeContext = createContext<PracticeContext>({
	currentPracticeObject: {},
	showPayments: true,
	planFilterOptions: {
		startDate: '',
		endDate: '',
		filterOn: PLANS_FILTER_ON_OPTIONS.APPLIED_DATE,
		quickFilterValue: PLANS_QUICK_FILTERS.ALL,
		searchQuery: ''
	}
})

export function useCurrentPractice() {
	return useContext(CurrentPracticeContext)
}

const getStoredPracticeId = (): string | null => {
	try {
		return localStorage?.getItem(PRACTICE_ID_STORAGE_KEY)
	} catch (err) {
		console.warn('Failed to read from localStorage:', err)
		return null
	}
}

const setStoredPracticeId = (practiceId: string): void => {
	try {
		localStorage?.setItem(PRACTICE_ID_STORAGE_KEY, practiceId)
	} catch (err) {
		console.warn('Failed to write to localStorage:', err)
	}
}

const removeStoredPracticeId = (): void => {
	try {
		localStorage?.removeItem(PRACTICE_ID_STORAGE_KEY)
	} catch (err) {
		console.warn('Failed to remove from localStorage:', err)
	}
}

export function PracticeProvider({ children }: { children: ReactNode }) {
	const context = useSelector(selectContext)
	const user = context?.user
	const dispatch = useDispatch<AppDispatch>()
	const [searchParams, setSearchParams] = useSearchParams()
	const queryPracticeId = searchParams.get('practiceId')
	const isAuthCheckLoading = useSelector(selectIsAuthCheckLoading)
	const useConsolidated = useConsolidatedPractice()

	const primaryPractice =
		user?.primaryClinic ||
		user?.stripeClinicId ||
		user?.stripeClinicID ||
		(user?.financingStatus === 'active' && user?.practices?.[0]?.practice?.id)
	const [practiceID, setPracticeID] = useState<string>()
	const [clinicName, setClinicName] = useState()
	const [terminalLocation, setTerminalLocation] = useState()
	const [currentPracticeObject, setCurrentPracticeObject] = useState<IPractice>({})
	const [disputesNotificationCount, setDisputesNotificationCount] = useState<number | undefined>(0)
	const [currentConsolidator, setCurrentConsolidator] = useState<IConsolidator | undefined>(
		undefined
	)
	const [currentUserConsolidator, setCurrentUserConsolidator] = useState<IConsolidator | undefined>(
		undefined
	)
	const [currentPracticeIndustry, setCurrentPracticeIndustry] = useState<PimsIndustry | undefined>(
		undefined
	)
	const [userRolePractice, setUserRolePractice] = useState({
		staff: false,
		hospitalManager: false,
		regionalManager: false
	})
	const [dashboardUserId, setDashboarUserId] = useState<number>()
	const [planFilterOptions, setPlanFilterOptions] = useState<PracticePlanFilterOptions>({
		startDate: getOlderDate(90),
		endDate: format(new Date(), 'yyyy-MM-dd'),
		filterOn: PLANS_FILTER_ON_OPTIONS.APPLIED_DATE,
		quickFilterValue: PLANS_QUICK_FILTERS.ALL,
		searchQuery: ''
	})
	const quickPlanFilterSetter = useCallback(
		(filter: string) => () =>
			setPlanFilterOptions((prev) => ({ ...prev, quickFilterValue: filter })),
		[]
	)
	const clearPlanFilters = useCallback(() => {
		setPlanFilterOptions((prev) => ({
			...prev,
			quickFilterValue: PLANS_QUICK_FILTERS.ALL,
			filterOn: PLANS_FILTER_ON_OPTIONS.APPLIED_DATE,
			searchQuery: ''
		}))
	}, [])

	const { data: practicesData, error: practicesError } = usePractices(context, practiceID)

	if (practicesError) {
		console.error(practicesError)
	}

	const updatePracticeID = useCallback((newPracticeId: string) => {
		setPracticeID(newPracticeId)
		setStoredPracticeId(newPracticeId)
	}, [])

	// Initialize practiceID with priority: URL param > localStorage > primaryPractice
	useEffect(() => {
		if (isAuthCheckLoading) {
			return
		}
		if (!user) {
			setPracticeID(undefined)
			removeStoredPracticeId()
			return
		}

		if (queryPracticeId) {
			// URL has practiceId - use it, store it, then clear URL param
			updatePracticeID(queryPracticeId)

			// Clear the query parameter from URL
			const newSearchParams = new URLSearchParams(searchParams)
			newSearchParams.delete('practiceId')
			setSearchParams(newSearchParams, { replace: true })
		} else {
			// Check localStorage first, then fall back to primaryPractice
			const storedPracticeId = getStoredPracticeId()

			if (storedPracticeId) {
				setPracticeID(storedPracticeId)
			} else if (primaryPractice) {
				updatePracticeID(primaryPractice)
			}
		}
	}, [isAuthCheckLoading, user, queryPracticeId, primaryPractice])

	// updates currentPracticeIndustry when practicesData changes
	useEffect(() => {
		if (practicesData?.clinicIndustry) {
			setCurrentPracticeIndustry(
				stringToEnumValue<typeof PimsIndustry, PimsIndustry>(
					PimsIndustry,
					practicesData.clinicIndustry
				) ?? PimsIndustry.All
			)
		} else {
			setCurrentPracticeIndustry(undefined)
		}
	}, [practicesData])

	// Clear stored practice when user logs out
	useEffect(() => {
		if (!isAuthCheckLoading && !user) {
			removeStoredPracticeId()
		}
	}, [isAuthCheckLoading, user])

	// updates currentPracticeObject when changing locations
	useEffect(() => {
		// determines user's role at currentPracticeObject
		const handleUserRolePractice = () => {
			if (!user || !user.uid || !currentPracticeObject) {
				return
			}
			setUserRolePractice({
				staff:
					currentPracticeObject?.staffUsers?.some((staffUser) => staffUser === user.uid) || false,
				hospitalManager:
					currentPracticeObject?.hmUsers?.some((hmUser) => hmUser === user.uid) || false,
				regionalManager: currentPracticeObject?.regionalManagerId === user.uid || false
			})
		}

		/**
		 * Applies the practice's (or its consolidator's) session persistence mode to
		 * Firebase. SESSION ends the session when the browser/tab closes; LOCAL (the
		 * Firebase default) persists across browser restarts. Practice-level setting
		 * takes precedence over consolidator. Always called so switching from a
		 * SESSION-mode practice to a LOCAL-mode one correctly resets persistence.
		 */
		function handleSessionPersistenceMode(practice: IPractice) {
			const mode: SessionPersistenceMode =
				practice.settings?.sessionPersistenceMode ??
				practice.consolidator?.featureFlags?.sessionPersistenceMode ??
				'LOCAL'
			const persistence =
				mode.toUpperCase() === 'SESSION' ? browserSessionPersistence : browserLocalPersistence
			setPersistence(authClient(), persistence).catch((err) =>
				console.error('Failed to set session persistence:', err)
			)
		}

		async function loadConsolidatorData() {
			const consolidatorData =
				practicesData?.consolidatorId &&
				(await getConsolidator(context, practicesData.consolidatorId))
			if (consolidatorData && consolidatorData.exists()) {
				const data = consolidatorData.data()
				setCurrentConsolidator(data)
				CustomizedLabels.customLabels = data?.customization
				customFields.customFields = data?.customFields
				if (user?.consolidatorId?.toString() === practicesData?.consolidatorId?.toString()) {
					setCurrentUserConsolidator(data)
				}
			} else {
				setCurrentConsolidator(undefined)
				CustomizedLabels.customLabels = null
				customFields.customFields = null
			}
			if (!user || !user.consolidatorId) {
				return
			}

			if (user?.consolidatorId !== practicesData?.consolidatorId && isScratchAdminUser(user)) {
				const userConsolidatorData = await getConsolidator(context, user.consolidatorId)
				if (userConsolidatorData && userConsolidatorData.exists()) {
					// eslint-disable-next-line @typescript-eslint/ban-ts-comment
					// @ts-ignore: if exists() is true, data() should not be undefined
					const data = userConsolidatorData.data()
					CustomizedLabels.customLabels = data?.customization
					setCurrentUserConsolidator(data as IConsolidator)
				} else {
					setCurrentUserConsolidator(undefined)
				}

				const practiceConsolidator = await getConsolidator(
					practicesData,
					practicesData.consolidatorId
				)
				if (practiceConsolidator && practiceConsolidator.exists()) {
					// eslint-disable-next-line @typescript-eslint/ban-ts-comment
					// @ts-ignore: if exists() is true, data() should not be undefined
					const data = practiceConsolidator.data()
					setCurrentConsolidator(data as IConsolidator)
					CustomizedLabels.customLabels = data?.customization
					customFields.customFields = data?.customFields
				} else {
					setCurrentConsolidator(undefined)
				}
			}
		}

		if (!user) {
			setPracticeID(undefined)
			setCurrentPracticeObject({})
			setTerminalLocation(undefined)
			handleUserRolePractice()
			setCurrentConsolidator(undefined)
			return
		}
		if (practicesData) {
			// Set the consolidator data before the practice object so you don't get any flashing
			setCurrentPracticeObject(practicesData)
			setTerminalLocation(practicesData.terminalLocation)

			handleSessionPersistenceMode(practicesData)
			setClinicName(practicesData.clinicName)
			handleUserRolePractice()
			loadConsolidatorData()
			setDashboarUserId(user.dashboardUserId)
			if (practicesData?.settings?.isAppointmentRequestsEnabled) {
				dispatch(fetchAppointmentRequestsCount({ practiceId: practicesData.id }))
				// Update Appointment Requests count every 60 seconds
				const intervalId = setInterval(() => {
					dispatch(fetchAppointmentRequestsCount({ practiceId: practicesData.id }))
				}, 60000)
				return () => clearInterval(intervalId)
			}
		}
		// eslint wants currentPracticeobject and terminal location in the dpendencies, but that causes maximum updates.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [practicesData, user])

	// creates terminal location if no terminal
	useEffect(() => {
		try {
			if (!user || !currentPracticeObject) {
				// console.log('do not create new location');
				return
			}
			// console.log(terminalLocation);
			if (
				user &&
				currentPracticeObject?.cuid &&
				currentPracticeObject?.terminalLocation === undefined &&
				practicesData?.terminalLocation === undefined &&
				terminalLocation === undefined
			) {
				// console.log('create a new terminal location');
				const fetch = async () => {
					// if (!currentPracticeObject?.clinicAddress || !currentPracticeObject?.clinicCountry) {
					//   console.log('clinic needs an address, canceling terminal location creation');
					//   return;
					// }
					const responseTerminalLocation = await fetchPostJSON(
						'/api/create_stripe_terminal_location',
						{
							display_name: currentPracticeObject?.cuid,
							'address[line1]': currentPracticeObject?.clinicAddress,
							'address[city]': currentPracticeObject?.clinicCity,
							'address[country]': currentPracticeObject?.clinicCountry,
							'address[postal_code]': currentPracticeObject?.clinicZip,
							'address[state]': currentPracticeObject?.clinicState,
							'metadata[connected_account]': currentPracticeObject?.cuid
						}
					)
					// console.log('Create terminalLocation RESPONSE', responseTerminalLocation);
					const createTerminalLocation = await responseTerminalLocation.id
					updatePractice(currentPracticeObject?.cuid, {
						terminalLocation: createTerminalLocation
					})
					// console.log('terminalLocation Added to Practice');
				}
				fetch().catch((e) => e)
			}
		} catch (err) {
			// console.log(err);
		}
	}, [currentPracticeObject, user])

	const {
		status: disputesNotificationsStatus,
		data: disputesNotifications,
		error: disputesNotificationsError
	} = useDisputesByEvidenceNeeded(currentPracticeObject?.stripeAccountId)

	// Check if isActive is strictly false to determine inactivity
	const isCurrentPracticeActive = useMemo(
		() => currentPracticeObject?.isActive !== false,
		[currentPracticeObject?.isActive]
	)

	const showPayments = useMemo(() => {
		if (!user || !currentPracticeObject) {
			// By default, show payments
			return true
		}

		const userPractice = user?.practices?.find(
			(up) => up?.practice?.id === currentPracticeObject?.id?.replace('acct_', 'prac_')
		)

		// Only hide payments if flag is explicitly set to false
		return userPractice?.showPayments !== false
	}, [user, currentPracticeObject])

	if (disputesNotificationsError) {
		console.error(disputesNotificationsError)
	}

	useEffect(() => {
		if (disputesNotificationsStatus === STATUS_SUCCESS) {
			setDisputesNotificationCount(disputesNotifications?.length)
		}
	}, [currentPracticeObject, user, disputesNotifications])

	const isPaymentEnabled = useMemo(() => {
		if (!showPayments) return false
		return !!currentPracticeObject?.stripeAccountId
		// removing this for now, since consolidated practice isn't source of truth yet
		// currentPracticeObject?.consolidatedPractice?.isPaymentEnabled
	}, [currentPracticeObject, showPayments])

	const isFinancingEnabled = useMemo(
		() =>
			currentPracticeObject?.financingStatus === 'active' ||
			currentPracticeObject?.consolidatedPractice?.isLendingEnabled,
		[
			currentPracticeObject?.financingStatus,
			currentPracticeObject?.consolidatedPractice?.isLendingEnabled
		]
	)

	const canAccessFinancingDev = useMemo(
		() =>
			!!currentPracticeObject?.settings?.showFinancingDev ||
			hasFinancingDashboardAccess(currentPracticeObject, useConsolidated),
		[currentPracticeObject, useConsolidated]
	)

	const contexts = useMemo(
		() => ({
			currentPracticeObject,
			setCurrentPracticeObject,
			practiceID,
			setPracticeID: updatePracticeID,
			terminalLocation,
			userRolePractice,
			clinicName,
			disputesNotificationCount,
			currentConsolidator,
			currentUserConsolidator,
			dashboardUserId,
			isCurrentPracticeActive,
			showPayments,
			isPaymentEnabled,
			isFinancingEnabled,
			canAccessFinancingDev,
			planFilterOptions,
			setPlanFilterOptions,
			quickPlanFilterSetter,
			clearPlanFilters,
			currentPracticeIndustry
		}),
		[
			currentPracticeObject,
			practiceID,
			terminalLocation,
			userRolePractice,
			currentConsolidator,
			currentUserConsolidator,
			user,
			isCurrentPracticeActive,
			showPayments,
			isPaymentEnabled,
			isFinancingEnabled,
			canAccessFinancingDev,
			planFilterOptions,
			quickPlanFilterSetter,
			clearPlanFilters,
			currentPracticeIndustry
		]
	)

	return (
		<CurrentPracticeContext.Provider value={contexts}>{children}</CurrentPracticeContext.Provider>
	)
}

export default PracticeProvider
